//@flow
import React, { Component } from 'react';
import { EnhancedField } from 'common/components/core/form/Field';
import { themr } from 'react-css-themr';
import { withUnitConverter } from 'common/containers/intl';
import classnames from 'classnames';

import NumberUtils from 'common/utils/NumberUtils';
import Units from 'common/enums/UnitEnum';

import { Dimensions } from 'common/models/internal/Dimensions';

import defaultTheme from './DimensionsInput.scss';

import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';

type Props = {
    theme: Object,
    defaultValue: ?Dimensions,
    unitConverter: UnitConversionHelper,
    refGetter: DimensionsInput => void
};

type State = {
    length: ?(number | string),
    breadth: ?(number | string),
    height: ?(number | string),
    lengthError: ?string,
    breadthError: ?string,
    heightError: ?string
};

const preferredUnits = [Units.list.length.Centimeter, Units.list.length.Inch];
const sourceUnit = Units.list.length.Meter;

class DimensionsInput extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            length: null,
            breadth: null,
            height: null,
            lengthError: null,
            breadthError: null,
            heightError: null
        };
        const { defaultValue } = props;
        if (defaultValue) {
            const { length, breadth, height } = defaultValue;
            this.state = {
                ...this.state,
                length: NumberUtils.isNumeric(length)
                    ? this.convertToPreferredUnit(length)
                    : length,
                breadth: NumberUtils.isNumeric(breadth)
                    ? this.convertToPreferredUnit(breadth)
                    : breadth,
                height: NumberUtils.isNumeric(height)
                    ? this.convertToPreferredUnit(height)
                    : height
            };
        }
    }

    componentDidMount = () => {
        const { refGetter } = this.props;
        refGetter && refGetter(this);
    };

    UNSAFE_componentWillReceiveProps = nextProps => {
        if (this.props.unitConverter !== nextProps.unitConverter) {
            const { length, breadth, height } = this.state;
            this.setState({
                length: NumberUtils.isNumeric(length)
                    ? this.convertToPreferredUnit(NumberUtils.toNumber(length))
                    : length,
                breadth: NumberUtils.isNumeric(breadth)
                    ? this.convertToPreferredUnit(NumberUtils.toNumber(breadth))
                    : breadth,
                height: NumberUtils.isNumeric(height)
                    ? this.convertToPreferredUnit(NumberUtils.toNumber(height))
                    : height
            });
        }
    };

    convertToPreferredUnit = (value: string | number): number => {
        const { unitConverter } = this.props;
        const convertedValue = unitConverter.convertMeters({
            value,
            preferredUnits
        }).value;

        if (typeof convertedValue !== 'number') {
            throw new Error(`Bad number: ${value}`);
        }
        return convertedValue;
    };

    convertToSourceUnit = (value: string | number): number => {
        const { unitConverter } = this.props;
        const preferredUnit = unitConverter.getPreferredUnit({
            preferredUnits,
            sourceUnit
        });
        const converted = unitConverter.basicConvert({
            value: NumberUtils.toNumber(value).toFixed(2),
            sourceUnit: preferredUnit,
            targetUnit: sourceUnit
        });
        if (typeof converted !== 'number') {
            throw new Error(`Bad number: ${value}`);
        }
        return converted;
    };

    handleLengthChange = (value: string) => {
        this.handleDimensionChange('length', value);
    };

    handleBreadthChange = (value: string) => {
        this.handleDimensionChange('breadth', value);
    };

    handleHeightChange = (value: string) => {
        this.handleDimensionChange('height', value);
    };

    handleDimensionChange = (
        type: 'length' | 'breadth' | 'height',
        value: string
    ) => {
        this.setState(state => {
            const currentValue = state[type];
            const isNumeric = NumberUtils.isNumeric(value);
            // init it 0 if no existing value to make sure the component becomes controlled
            // We dont want to set an initial state value 0, so that the component looks unfilled.
            return {
                [type]: isNumeric ? value : currentValue || 0
            };
        });
    };

    setValidationErrors() {
        this.setState(state => {
            const { length, breadth, height } = state;
            if (this.isValid()) {
                //no issues.
                return {};
            }
            const errorStates: { [key: string]: ?string } = {
                lengthError: null,
                breadthError: null,
                heightError: null
            };
            if (!NumberUtils.isNumeric(length)) {
                errorStates.lengthError = 'length not set';
            }
            if (!NumberUtils.isNumeric(breadth)) {
                errorStates.breadthError = 'breadth not set';
            }
            if (!NumberUtils.isNumeric(height)) {
                errorStates.heightError = 'height not set';
            }
            return errorStates;
        });
    }

    getDimensions(): ?Dimensions {
        const { length, breadth, height } = this.state;
        if (!length || !breadth || !height) {
            return null;
        }
        return Dimensions.fromJS({
            length: this.convertToSourceUnit(length),
            breadth: this.convertToSourceUnit(breadth),
            height: this.convertToSourceUnit(height)
        });
    }

    isValid(): boolean {
        const { length, breadth, height } = this.state;
        if (length === null && breadth === null && height === null) {
            // No value is good value!
            return true;
        }
        return (
            NumberUtils.isNumeric(length) &&
            NumberUtils.isNumeric(breadth) &&
            NumberUtils.isNumeric(height)
        );
    }

    render() {
        const {
            length,
            breadth,
            height,
            lengthError,
            breadthError,
            heightError
        } = this.state;
        const { theme, unitConverter } = this.props;
        const preferredUnitText = unitConverter.getConvertedUnitDisplayText({
            sourceUnit: Units.list.length.Meter,
            preferredUnits,
            pluralizeUnit: false
        });
        return (
            <ThemedBasicDimensionsInput
                theme={theme}
                length={length}
                breadth={breadth}
                height={height}
                unitText={preferredUnitText}
                lengthError={lengthError}
                breadthError={breadthError}
                heightError={heightError}
                onLengthChange={this.handleLengthChange}
                onBreadthChange={this.handleBreadthChange}
                onHeightChange={this.handleHeightChange}
            />
        );
    }
}

type BasicDimensionsInputProps = {
    ...State,
    unitText: ?string,
    theme: Object,
    t: (string, ?Object) => string,
    onLengthChange: string => void,
    onBreadthChange: string => void,
    onHeightChange: string => void
};

class BasicDimensionsInput extends Component<BasicDimensionsInputProps> {
    render() {
        const {
            t,
            theme,
            length,
            breadth,
            height,
            unitText,
            lengthError,
            breadthError,
            heightError,
            onLengthChange,
            onBreadthChange,
            onHeightChange,
            testId
        } = this.props;

        return (
            <div className={theme.wrapper}>
                <div className={classnames(theme.fieldGroup, theme.notLast)}>
                    <span className={classnames(theme.label, theme.subLabel)}>
                        {t('dimensionsInput.lengthLabel')}
                    </span>
                    <EnhancedField
                        className={theme.input}
                        type="text"
                        element="input"
                        value={length}
                        onChange={onLengthChange}
                        theme={theme}
                        error={lengthError}
                        rightIcon={unitText}
                        testId={testId?.lengthTestId}
                    />
                </div>
                <div className={classnames(theme.fieldGroup, theme.notLast)}>
                    <span className={classnames(theme.label, theme.subLabel)}>
                        {t('dimensionsInput.breadthLabel')}
                    </span>
                    <EnhancedField
                        className={theme.input}
                        type="text"
                        element="input"
                        value={breadth}
                        onChange={onBreadthChange}
                        theme={theme}
                        error={breadthError}
                        rightIcon={unitText}
                        testId={testId?.breadthTestId}
                    />
                </div>
                <div className={theme.fieldGroup}>
                    <span className={classnames(theme.label, theme.subLabel)}>
                        {t('dimensionsInput.heightLabel')}
                    </span>
                    <EnhancedField
                        className={theme.input}
                        type="text"
                        element="input"
                        value={height}
                        onChange={onHeightChange}
                        theme={theme}
                        error={heightError}
                        rightIcon={unitText}
                        testId={testId?.heightTestId}
                    />
                </div>
            </div>
        );
    }
}

const ThemedBasicDimensionsInput = themr(
    null,
    defaultTheme
)(BasicDimensionsInput);

export { ThemedBasicDimensionsInput as BasicDimensionsInput };
export default withUnitConverter(DimensionsInput);
